import { apiService } from '.';
import { ChannelTypes } from 'store/types/channeTypes';
import { CheckDigitPolicy } from 'store/types/checkDigitPolicy';

const ConfigurationUri: string = '/session/start';

interface IConfiguration {
    baseApiUri: string;
    appInitialState: AppInitialState;
    supportedChannelType: ChannelTypes;
    phoneNumberDigitCheck: CheckDigitPolicy;
    enableErrorCallback?: boolean;
    sessionId: string;
}

export interface IConfigurationInitResponse {
    state: ConfigurationState;
    supportedDisplayChannel: ChannelTypes;
    phoneNumberDigitCheck: CheckDigitPolicy;
    exception?: any;
}

export type ConfigurationState = 'Loaded' | 'Error' | 'Locked' | 'Loading';
export type AppInitialState = 'Unknown' | 'DocumentLocked' | 'VerificationWorkflow';

const defaultConfiguration: IConfiguration = {
    baseApiUri: 'http://verify.localhost/',
    appInitialState: 'Unknown',
    supportedChannelType: 'SMSandPhoneCall',
    phoneNumberDigitCheck: 'None',
    sessionId: ''
};

const getConfigurationValue = (itemName: keyof typeof window): string => {
    let itemValue: string|undefined = undefined;

    try {
        itemValue = window.sessionStorage.getItem(itemName as string)?.toString();
    } catch (e) {
        // Handle old browser without session storage
    } finally {
        if (!itemValue) {
            //itemValue = (window as any)[itemName];
            itemValue = window[itemName];
        }
    }

    if (!itemValue) {
        console.error('Cannot find the value of', itemName);
    }

    return itemValue !== undefined ? itemValue : '';
};

const getClaimRequest = (): string => getConfigurationValue('claimRequest');

class ConfigurationService {
    private configuration: IConfiguration = defaultConfiguration;

    initialize = async (): Promise<IConfigurationInitResponse> => {
        try {
            this.configuration = await apiService.Post<IConfiguration>(ConfigurationUri, { claim_request: getClaimRequest() });
        } catch (err) {
            return { state: 'Error', supportedDisplayChannel: null, phoneNumberDigitCheck: 'None', exception: err };
        }

        if (!!this.configuration) {
            if (this.configuration.appInitialState === 'DocumentLocked') {
                return { state: 'Locked', supportedDisplayChannel: null, phoneNumberDigitCheck: 'None' };
            }
            return {
                state: 'Loaded',
                supportedDisplayChannel: this.configuration.supportedChannelType,
                phoneNumberDigitCheck: this.configuration.phoneNumberDigitCheck
            };
        }

        return { state: 'Error', supportedDisplayChannel: null, phoneNumberDigitCheck: 'None', exception: 'Empty configuration' };
    };

    getBaseApiUri = (): string => this.configuration.baseApiUri;

    enableErrorCallback = (): boolean => this.configuration.enableErrorCallback || false;

    sessionId = (): string => this.configuration.sessionId || '';

    kazmonEndpointUrl = (): string => getConfigurationValue('kazmonEndpointUrl');

    kazmonInstrumentationKey = (): string => getConfigurationValue('kazmonInstrumentationKey');

    environmentName = (): string => getConfigurationValue('environmentName');

    buildVersion = (): string => getConfigurationValue('buildVersion');
}

export let configuration = new ConfigurationService();
